<template>
  <div :class="['sidebar', { 'dark-mode': isDarkMode, collapsed: collapsed }]">
    <div class="sidebar-inner">
      <div class="sidebar-toggle-wrapper">
        <button class="toggle-button" @click="toggleSidebar">
          <img :src="collapsed
            ? (isDarkMode
              ? require('@/assets/icons/sidebar-expand-dark.svg')
              : require('@/assets/icons/sidebar-expand.svg'))
            : (isDarkMode
              ? require('@/assets/icons/sidebar-collapse-dark.svg')
              : require('@/assets/icons/sidebar-collapse.svg'))" alt="Toggle Sidebar" class="toggle-icon">
        </button>
      </div>

      <div class="top-section">

        <div class="new-chat-container">
          <button class="new-chat" @click="startNewChat">
            <div class="new-chat-content">
              <img
                :src="isDarkMode ? require('@/assets/icons/new-chat-dark.svg') : require('@/assets/icons/new-chat.svg')"
                alt="Plus Icon" class="icon" />
              <span>New chat</span>
            </div>
          </button>
        </div>

        <div class="search-container">
          <button class="search" @click="toggleSearchBar">
            <div class="search-content">
              <img :src="isDarkMode ? require('@/assets/icons/search-dark-theme.svg') : require('@/assets/icons/search-light-theme.svg')"
                alt="Search Icon" class="icon">
              <span>Search Chats</span>
            </div>
          </button>
        </div>

        <div v-if="showSearchBar" class="search-bar">
          <input
          v-model="searchQuery"
          placeholder="Search Conversations..."
          class="search-input">
          <button class="clear-btn" @click="clearSearch">
            <img :src="isDarkMode ? require('@/assets/icons/clear-btn-dark-theme.svg') : require('@/assets/icons/clear-btn-light-theme.svg')"
              alt="Clear Search" class="clear-icon">
          </button>
        </div>

        <div class="conversations-section">
          <div class="conversations">
            <div v-for="conversation in filteredConversations" :key="conversation.id" class="conversation-item"
              @click="loadConversation(conversation)"
              :class="{ 'selected': conversation.id === selectedConversationId }">
              <img
                :src="isDarkMode ? require('@/assets/icons/chat-icon-white.svg') : require('@/assets/icons/chat-icon.svg')"
                alt="Chat Icon" class="icon" />

              <div class="conversation-title">

                <template v-if="editingId === conversation.id">
                  <input 
                    v-model="tempName"
                    v-autofocus
                    :ref="`rename_${conversation.id}`"
                    @keyup.enter="renameConversation(conversation)"
                    @blur="cancelEdit()"
                    class="rename-input"
                    :placeholder="conversation.name"
                    autofocus
                  />
                </template>
                <template v-else>
                  {{ conversation.name }}
                </template>
              </div>

              <img
                v-if="editingId !== conversation.id"
                class="edit-icon"
                :src="isDarkMode ? require('@/assets/icons/rename-dark-mode.svg') : require('@/assets/icons/rename-light-mode.svg')" 
                alt="Edit"
                @click="startEdit(conversation)">   
            </div>
          </div>
        </div>

      </div>

    </div>

    <div :class="['bottom-section', { 'dark-mode': isDarkMode }]">
      <div class="menu-item" @click="clearConversation">
        <img
          :src="isDarkMode ? require('@/assets/icons/clear-icon-dark.svg') : require('@/assets/icons/clear-icon.svg')"
          alt="Clear conversation Icon" />
        <span>Clear Conversation</span>
      </div>

      <div class="menu-item" @click="toggleDarkMode">
        <img :src="modeIcon" alt="Mode Icon" />
        <span>{{ isDarkMode ? 'Dark mode' : 'Light mode' }}</span>
      </div>

      <div v-show="false" class="menu-item" @click="$emit('show-faq')">
        <img :src="isDarkMode ? require('@/assets/icons/faq-icon-dark.svg') : require('@/assets/icons/faq-icon.svg')"
          alt="Updates & FAQ Icon" />
        Updates & FAQ
      </div>

      <div class="menu-item" @click="$emit('logout')">
        <img
          :src="isDarkMode ? require('@/assets/icons/logout-icon-dark.svg') : require('@/assets/icons/logout-icon.svg')"
          alt="Log out Icon" />
        <span>Log Out</span>
      </div>

    </div>
  </div>
</template>

<script>
import api from '@/axios-config';

export default {
  directives: {
    autofocus: {
      inserted(el) { el.focus(); el.select && el.select(); },
      mounted(el) { el.focus(); el.select && el.select(); },
    }
  },
  name: 'AppSidebar',
  props: { conversations: Array, conversationMessagesMap: Object },
  data() {
    return {
      selectedConversationId: null,
      isDarkMode: localStorage.getItem('isDarkMode') === 'true',
      collapsed: false,
      showSearchBar: false,
      searchQuery: '',
      editingId: null,
      tempName: '',
      screenWidth: window.innerWidth, // Store the current width of the window
    };
  },
  computed: {
    filteredConversations() {
      if (!this.searchQuery) return this.conversations;

      const query = this.searchQuery.toLowerCase();

      return this.conversations.filter((convo) => {
        const titleMatch = convo.name?.toLowerCase().includes(query);

        const messages = this.conversationMessagesMap?.[convo.resource_id] || [];

        const historyMatch = messages.some((msg) => {
          const content = msg?.html_content ?? msg?.content ?? '';
          return content.toLowerCase().includes(query);
        });

        return titleMatch || historyMatch;
      });
    },
    modeIcon() {
      return this.isDarkMode
        ? require('@/assets/icons/dark-mode-icon.svg')
        : require('@/assets/icons/light-mode-icon.svg');
    },
  },
  methods: {
    startEdit(convo) {
      this.editingId = convo.id;
      this.tempName = convo.name;
      this.$nextTick(() => {
        const el = this.$refs[`rename_${convo.id}`];
        if (el) el.select();
      });
    },
    cancelEdit() {
      this.editingId = null;
      this.tempName = '';
    },

    async renameConversation(convo) {
      const newName = (this.tempName || '').trim();
      if (!newName || newName === convo.name) {
        this.cancelEdit();
        return;
      }

      await api.patch(`/api/conversation/${convo.id}/rename/`, { name: newName });
      this.$emit('refresh-conversations');

      this.cancelEdit();
    },

    getFirstMessage(conversation) {
      const messages = this.conversationMessagesMap?.[conversation.resource_id] || [];
      return messages.length ? messages[0].content : '';
    },
    clearSearch() {
      this.searchQuery = '';
    },
    toggleSearchBar() {
      this.openSidebar();
      this.showSearchBar = !this.showSearchBar;
      this.searchQuery = '';
    },
    loadConversation(conversation) {
      this.selectedConversationId = conversation.id;
      this.$emit('load-conversation', conversation);
    },
    startNewChat() {
      console.log("Sidebar: New Chat clicked, resetting conversation.");
      this.selectedConversationId = null;
      this.$emit('new-chat');
    },
    clearConversation() {
      if (this.selectedConversationId) {
        this.$emit('clear-conversation', this.selectedConversationId);
        this.selectedConversationId = null;
      }
    },
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      this.$emit('toggle-dark-mode', this.isDarkMode); // Emit event to parent
    },
    toggleSidebar() {
      this.collapsed = !this.collapsed;
      this.$emit('collapsed', this.collapsed);
    },
    openSidebar() {
      if (this.collapsed) {
        this.collapsed = false;
        this.$emit('collapsed', false);
      }
    },
    handleCollapse(val) {
      this.isCollapsed = val;
    },

    // Dynamically collapse the sidebar based on the screen width
    handleResize() {
      this.screenWidth = window.innerWidth;

      // Collapse the sidebar if the width is less than 768px
      if (this.screenWidth < 768) {
        this.collapsed = true;
      } else {
        this.collapsed = false;
      }
    },
  },
  mounted() {
    // Add an event listener to watch for window resizing
    window.addEventListener('resize', this.handleResize);

    // Initial resize check on component mount
    this.handleResize();
  },
  beforeUnmount() {
    // Clean up the event listener
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
.sidebar {
  width: 20rem;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-right: 1px solid rgba(28, 28, 28, 0.1);
  padding: 20px;
  box-sizing: border-box;
  font-family: Inter, Helvetica, sans-serif;
  z-index: 2;
  transition: width 0.3s ease;
  /* Ensures it stays above the footer */
}

.sidebar-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-toggle-wrapper {
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 1rem 0.5rem 0.5rem;
  width: 100%;
}

.sidebar.collapsed {
  width: 60px;
}

.sidebar.collapsed .conversation-item {
  display: none;
}

.sidebar.collapsed .new-chat span {
  display: none;
}

.sidebar.collapsed .search span {
  display: none;
}

.sidebar.collapsed .search .icon {
  margin: 0;
  margin-right: 0;
  padding: 0;
}

.sidebar.collapsed .new-chat {
  height: 40px;
  padding: 0;
  background: transparent;
}

.sidebar.collapsed .new-chat .icon {
  margin: 0;
}

.sidebar.collapsed .search-bar {
  display: none;
}

.sidebar.collapsed .menu-item span {
  display: none;
}

.sidebar.collapsed .search-container:hover {
  background-color: transparent;
}

.toggle-button {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 18px;
  margin-left: 3px;
}

.toggle-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.top-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-height: calc(100vh - 200px);
}

.conversations-section {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  gap: 20px;
}

.conversations-section::-webkit-scrollbar {
  width: 8px;
}

.conversations-section::-webkit-scrollbar-track {
  background: transparent;
}

.conversations-section::-webkit-scrollbar-thumb {
  background-color: #555;
  border-radius: 10px;
}

.conversations-section::-webkit-scrollbar-thumb:hover {
  background-color: #888;
}


.new-chat-container {
  position: sticky;
  top: 0;
  z-index: 1;
}

.new-chat {
  width: 100%;
  height: 40px;
  border: none;
  padding: 8px 0px;
  background: white;
  color: black;
  font-size: 0.88rem;
  transition: all .3s cubic-bezier(.05, .03, .35, 1);
  background-image: linear-gradient(#8ECAE6 0, #8ECAE6 100%);
  border-radius: 5px;
  cursor: pointer;
  margin-top: 15px;
  opacity: 1;
}

.new-chat:hover {
  opacity: 0.85;
}

.new-chat-content {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
}

.new-chat .icon {
  width: 24px;
  height: 24px;
  margin-right: 8px;
}

.search-container {
  padding-top: 5px;
  padding-bottom: 5px;
}
.search {
  background: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  border: none;
  font-size: 0.88rem;
  color: black;
  padding: 0;
}
.dark-mode .search {
  color: white;
}

.search-content {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0;
}

.search-container:hover {
  background-color: #e4e4e4;
  border-radius: 5px;
}

.dark-mode .search-container:hover {
  background-color: #464646;
  border-radius: 5px;
}

.search .icon {
  width: 24px;
  height: 24px;
  margin-right: 5px;
}

.dark-mode .new-chat {
  color: white;
  background: #219ebc;
}

.search-bar {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  padding-right: 15px;
  border-radius: 4px;
  border: 1px solid #ccc;
  outline: none;
  background-color: white;
  color: black;
}

.dark-mode .search-bar {
  background-color: #2c2c2c;
  color: white;
  border: 1px solid #555;
  border-radius: 4px;
}

.search-input {
  width: 100%;
  padding: 8px;
  padding-right: 16px;
  box-sizing: border-box;
  border: none;
  background-color: transparent;
  border-radius: 4px;
  outline: none;
}

.dark-mode .search-input {
  color: white;
}

.clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  z-index: 1;
}

.clear-icon {
  width: 16px;
  height: 16px;
  opacity: 0.7;
}

.clear-icon:hover {
  opacity: 1;
}

.conversations .icon {
  width: 24px;
  height: 24px;
  margin-right: 5px;
}

.conversations {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.conversation-item {
  display: flex;
  align-items: center;
  padding: 8px 0px;
  font-size: 0.88rem;
  color: #1C1C1C;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 5px;
}

.conversation-item:hover {
  background-color: #d3d3d3;
}

.conversation-item.selected {
  background-color: #E8F0FE;
  /* Highlight color */
  border-radius: 8px;
}

.conversation-title {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  /* Keep text on single line */
  overflow: hidden;
  /* Hide overflow text */
  text-overflow: ellipsis;
  /* Show ellipsis for truncated text */
  line-height: 1.3;
}

.rename-input {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding:6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  line-height: 1.2;
  background-color: white;
  color: black;
}

.rename-input:focus {
  outline: none;
}

.sidebar.dark-mode .rename-input {
  background-color: #2c2c2c;
  color: white;
  border: 1px solid #555;
}

.edit-icon {
  flex: 0 0 auto;
  opacity: 0;
  pointer-events: none;
  width: 16px;
  height: 16px;
  cursor: pointer;
  margin-left: 8px;
  margin-right: 8px;
  transition: opacity 0.15s ease;
}

.conversation-item:hover .edit-icon,
.conversation-item:focus-within .edit-icon {
  opacity: 1;
  pointer-events: auto;
}

.bottom-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 20px;
  flex-shrink: 0;
  border-top: 1px solid rgba(28, 28, 28, 0.1);
  /* Ensure the top border is visible */
  background-color: white;
  /* Ensure background matches the sidebar */
  z-index: 1;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
  color: #1C1C1C;
  cursor: pointer;
}

.menu-item img {
  width: 24px;
  height: 24px;
}

/* Dark Mode Styles */
.dark-mode .sidebar {
  background-color: rgb(24, 24, 24);
  color: #E0E0E0;
  border-right: 1px solid #333;
}

.dark-mode .conversation-item {
  color: white;
}

.dark-mode .conversation-item:hover {
  background-color: #464646;
}

.dark-mode .conversation-item.selected {
  background-color: #333;
}

.dark-mode .bottom-section {
  background-color: rgb(24, 24, 24);
  /* Ensure background matches dark mode */
  border-top: 1px solid #333;
}

.dark-mode .menu-item {
  color: white;
}

.dark-mode .menu-item img {
  filter: none;
  /* Remove filter as we're using specific dark mode icons */
}

.dark-mode .menu-item:hover {
  background-color: #333;
}

</style>